/**
 * Set And Forget Page
 * Consolidated view for automated notifications:
 * - Appointment Reminders
 * - Form Triggers (aggregated across all form templates)
 */

import React, { useState, useEffect, forwardRef, useImperativeHandle, useRef } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { Bell, FileText, Plus, Trash2, Mail, MessageSquare, Check, Clock, AlertCircle, Eye, X, ChevronLeft, Smartphone, Monitor, ExternalLink, Code, ChevronDown, ScrollText } from 'lucide-react';
import OverdueVaccineLogsPage from '../components/notifications/OverdueVaccineLogsPage';
import { useAuth } from '../contexts/AuthContext';
import { API_ENDPOINTS, API_BASE_URL } from '../config/api';
import {
  FormTemplate,
  FormTrigger,
  DeliveryMethod,
  getFormTemplates,
  getFormTriggers,
  createFormTrigger,
  updateFormTrigger,
  deleteFormTrigger,
  formatTriggerTime,
} from '../utils/formsApi';
import { NotificationTemplate, getTemplates } from '../utils/notificationsBroadcastApi';
import { generateEmailHtml } from '../components/notifications/builder/htmlEmailGenerator';
import { EmailDocument } from '../components/notifications/builder/types';

// ============================================================================
// Types
// ============================================================================

export interface SetAndForgetHandle {
  openCreate: () => void;
}

type SetAndForgetDeliveryMethod = 'sms' | 'email_plain' | 'email_html';
type ReminderType = 'appointment' | 'no_visit';

interface AppointmentReminderSetting {
  id: string;
  practice_id: string;
  minutes_before: number;
  delivery_method: SetAndForgetDeliveryMethod;
  is_enabled: boolean;
  message_template: string | null;
  template_id: string | null;
  from_email_address_id: string | null;
  created_at: string;
  updated_at: string;
}

interface NoVisitReminderSetting {
  id: string;
  practice_id: string;
  inactivity_minutes: number;
  cadence_minutes: number;
  delivery_method: SetAndForgetDeliveryMethod;
  is_enabled: boolean;
  message_template: string | null;
  template_id: string | null;
  created_at: string;
  updated_at: string;
}

type TimeUnit = 'minutes' | 'hours' | 'days';

interface ReminderForm {
  reminder_type: ReminderType;
  value: string;
  unit: TimeUnit;
  inactivity_value: string;
  inactivity_unit: TimeUnit;
  cadence_value: string;
  cadence_unit: TimeUnit;
  delivery_method: SetAndForgetDeliveryMethod;
  is_enabled: boolean;
  message_template: string;
  template_id: string | null;
}

interface FormTriggerWithTemplate extends FormTrigger {
  form_template_name: string;
  form_template_id: string;
}

interface TriggerForm {
  value: string;
  unit: TimeUnit;
  delivery_method: SetAndForgetDeliveryMethod;
  is_enabled: boolean;
  message_template: string;
  form_template_id: string;
  template_id: string | null;
}

// Default message templates (matches actual system defaults)
const DEFAULT_SMS_MESSAGE = "Reminder: {pet_name} has an appointment on {appointment_date} at {practice_name}. Call {practice_phone} to reschedule. Reply STOP to opt out.";
const DEFAULT_EMAIL_MESSAGE = "This is a friendly reminder that {pet_name} has an upcoming appointment on {appointment_date} at {practice_name}. Please arrive 10 minutes early. Call {practice_phone} to reschedule.";
const DEFAULT_FORM_SMS_MESSAGE = "{practice_name}: Please complete the form for {pet_name} before your appointment on {appointment_date}. {form_link}";
const DEFAULT_FORM_EMAIL_MESSAGE = "{practice_name}: Please complete the form for {pet_name} before your appointment on {appointment_date}. Click here: {form_link}";

type TabKey = 'reminders' | 'triggers' | 'overdue' | 'logs';

// ============================================================================
// Helper Functions
// ============================================================================

const convertToMinutes = (value: number, unit: TimeUnit): number => {
  switch (unit) {
    case 'days': return value * 1440;
    case 'hours': return value * 60;
    case 'minutes': return value;
  }
};

const convertFromMinutes = (minutes: number): { value: number; unit: TimeUnit } => {
  if (minutes >= 1440 && minutes % 1440 === 0) {
    return { value: minutes / 1440, unit: 'days' };
  } else if (minutes >= 60 && minutes % 60 === 0) {
    return { value: minutes / 60, unit: 'hours' };
  }
  return { value: minutes, unit: 'minutes' };
};

const formatMinutes = (minutes: number): string => {
  if (minutes >= 1440 && minutes % 1440 === 0) {
    const days = minutes / 1440;
    return `${days} day${days > 1 ? 's' : ''} before`;
  } else if (minutes >= 60 && minutes % 60 === 0) {
    const hours = minutes / 60;
    return `${hours} hour${hours > 1 ? 's' : ''} before`;
  }
  return `${minutes} minute${minutes > 1 ? 's' : ''} before`;
};

const formatDuration = (minutes: number): string => {
  if (minutes >= 1440 && minutes % 1440 === 0) {
    const days = minutes / 1440;
    return `${days} day${days > 1 ? 's' : ''}`;
  } else if (minutes >= 60 && minutes % 60 === 0) {
    const hours = minutes / 60;
    return `${hours} hour${hours > 1 ? 's' : ''}`;
  }
  return `${minutes} minute${minutes > 1 ? 's' : ''}`;
};

const getDeliveryIcon = (method: string) => {
  switch (method) {
    case 'sms': return <MessageSquare className="w-4 h-4" />;
    case 'email_plain': return <Mail className="w-4 h-4" />;
    case 'email_html': return <Code className="w-4 h-4" />;
    default: return <Mail className="w-4 h-4" />;
  }
};

const getDeliveryLabel = (method: string) => {
  switch (method) {
    case 'sms': return 'SMS';
    case 'email_plain': return 'Plain Email';
    case 'email_html': return 'HTML Email';
    default: return method;
  }
};

// Map old delivery method values to new ones (for loading from backend)
const normalizeDeliveryMethod = (method: string | null | undefined): SetAndForgetDeliveryMethod => {
  if (!method) return 'sms';
  const normalized = method.toLowerCase();
  if (normalized === 'sms') return 'sms';
  if (normalized === 'email' || normalized === 'email_plain') return 'email_plain';
  if (normalized === 'email_html') return 'email_html';
  // BOTH was an old option - default to sms
  if (normalized === 'both') return 'sms';
  return 'sms';
};

// Convert frontend delivery method to backend API format
// EMAIL = plain text email, EMAIL_HTML = rich HTML template, SMS = text message
const toBackendDeliveryMethod = (method: SetAndForgetDeliveryMethod): 'EMAIL' | 'EMAIL_HTML' | 'SMS' => {
  switch (method) {
    case 'sms': return 'SMS';
    case 'email_plain': return 'EMAIL';  // Plain email uses EMAIL
    case 'email_html': return 'EMAIL_HTML';
    default: return 'SMS';
  }
};

// Sample data for preview - follows model_field naming pattern
const SAMPLE_PREVIEW_DATA = {
  // Practice info
  practice_name: 'Happy Paws Veterinary',
  practice_phone: '(555) 123-4567',
  practice_address: '123 Main Street, Anytown, CA 94000',
  // Pet info
  pet_name: 'Max',
  pet_names: 'Max',
  // Appointment info
  appointment_date: 'Friday, January 3rd at 2:00 PM',
  // Last visit info (Appt Due reminders)
  last_visit_date: 'September 12, 2024',
  days_since_visit: '124',
  is_are: 'is',
  'is/are': 'is',
  book_appointment_link: 'https://helppet.ai/book/ca/san-francisco/happy-paws-vet',
  // Form info
  form_link: 'https://helppet.ai/forms/abc123-example',
  // Pet owner info - new naming pattern
  pet_owner_name: 'Jane Doe',
  pet_owner_first_name: 'Jane',
  pet_owner_email: 'jane.doe@email.com',
  pet_owner_phone: '(555) 987-6543',
  // Legacy aliases (for backwards compatibility)
  owner_name: 'Jane Doe',
  name: 'Jane Doe',
  first_name: 'Jane',
  email: 'jane.doe@email.com',
  phone: '(555) 987-6543',
};

// Apply placeholder substitutions - handles both {{key}} and {key} formats
const applyPlaceholders = (text: string, data: typeof SAMPLE_PREVIEW_DATA): string => {
  let result = text;
  for (const [key, value] of Object.entries(data)) {
    // Replace both {{key}} and {key} formats
    result = result.replace(new RegExp(`\\{\\{${key}\\}\\}`, 'g'), value);
    result = result.replace(new RegExp(`\\{${key}\\}`, 'g'), value);
  }
  return result;
};

// ============================================================================
// Preview Modal Component
// ============================================================================

interface PreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  deliveryMethod: SetAndForgetDeliveryMethod;
  messageTemplate: string | null;
  templateId: string | null;
  title: string;
  practiceName: string;
  practicePhone?: string;
}

const PreviewModal: React.FC<PreviewModalProps> = ({
  isOpen,
  onClose,
  deliveryMethod,
  messageTemplate,
  templateId,
  title,
  practiceName,
  practicePhone = '(555) 123-4567',
}) => {
  const [loading, setLoading] = useState(false);
  const [emailHtml, setEmailHtml] = useState<string | null>(null);
  const [notificationTemplate, setNotificationTemplate] = useState<NotificationTemplate | null>(null);
  const [viewMode, setViewMode] = useState<'mobile' | 'desktop'>('mobile');

  useEffect(() => {
    if (isOpen && templateId) {
      loadTemplate();
    } else if (isOpen) {
      setEmailHtml(null);
      setNotificationTemplate(null);
    }
  }, [isOpen, templateId]);

  // Build preview data with actual practice info and all supported placeholders
  const previewData = {
    // Practice info - use actual values
    practice_name: practiceName,
    practice_phone: practicePhone || '(555) 123-4567',
    practice_email: 'info@happypawsvet.com',
    practice_address: '123 Main Street, Anytown, CA 94000',
    // Pet info
    pet_name: 'Max',
    pet_names: 'Max',
    owner_pet_name: 'Max',
    // Appointment info
    appointment_date: 'Friday, January 3rd at 2:00 PM',
    next_appointment_date: 'Friday, January 3rd at 2:00 PM',
    // Last visit info (Appt Due reminders)
    last_visit_date: 'September 12, 2024',
    days_since_visit: '124',
    is_are: 'is',
    'is/are': 'is',
    has_have: 'has',
    book_appointment_link: 'https://helppet.ai/book/ca/san-francisco/happy-paws-vet',
    // Form info
    form_link: 'https://helppet.ai/forms/abc123-example',
    // Pet owner info - new naming pattern
    pet_owner_name: 'Jane Doe',
    pet_owner_first_name: 'Jane',
    pet_owner_email: 'jane.doe@email.com',
    pet_owner_phone: '(555) 987-6543',
    // Legacy aliases (for backwards compatibility)
    owner_name: 'Jane Doe',
    name: 'Jane Doe',
    first_name: 'Jane',
    email: 'jane.doe@email.com',
    phone: '(555) 987-6543',
    // Vaccine-specific
    vaccine_name: 'Rabies',
    scheduled_date: 'December 20, 2025',
    days_overdue: '27',
  };

  const loadTemplate = async () => {
    if (!templateId) return;
    
    setLoading(true);
    try {
      const templates = await getTemplates();
      const template = templates.find(t => t.id === templateId);
      if (template) {
        setNotificationTemplate(template);
        // If it has builder_document, generate HTML and apply placeholders
        if (template.builder_document) {
          let html = generateEmailHtml(template.builder_document as unknown as EmailDocument, {
            practiceName: practiceName,
          });
          // Apply placeholder replacements to the generated HTML
          html = applyPlaceholders(html, previewData);
          setEmailHtml(html);
        }
      }
    } catch (err) {
      console.error('Failed to load template for preview:', err);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const smsMessage = messageTemplate 
    ? applyPlaceholders(messageTemplate, previewData)
    : applyPlaceholders(DEFAULT_SMS_MESSAGE, previewData);

  const emailMessage = messageTemplate
    ? applyPlaceholders(messageTemplate, previewData)
    : applyPlaceholders(DEFAULT_EMAIL_MESSAGE, previewData);

  const showEmail = deliveryMethod === 'email_plain' || deliveryMethod === 'email_html';
  const showSms = deliveryMethod === 'sms';
  
  const subjectLine = notificationTemplate 
    ? applyPlaceholders(notificationTemplate.subject || 'Appointment Reminder', previewData)
    : 'Appointment Reminder';
  const dateStr = new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  const initials = practiceName.split(' ').map(w => w[0]).join('').substring(0, 2).toUpperCase();

  // iPhone Frame Component
  const IPhoneFrame: React.FC<{ children: React.ReactNode; type: 'sms' | 'email' }> = ({ children, type }) => (
    <div className="relative mx-auto" style={{ width: '280px' }}>
      <div className="relative rounded-[3rem] p-2 shadow-2xl" style={{ backgroundColor: '#1f2937' }}>
        <div className="absolute top-4 left-1/2 -translate-x-1/2 w-24 h-7 rounded-full z-20" style={{ backgroundColor: '#000' }} />
        <div className="relative rounded-[2.5rem] overflow-hidden" style={{ backgroundColor: '#000' }}>
          <div className="h-8" style={{ backgroundColor: '#fff' }} />
          <div className="px-6 pt-1 pb-1 flex items-center justify-between text-xs font-medium" style={{ backgroundColor: '#fff', color: '#111' }}>
            <span className="w-12">9:41</span>
            <div className="flex-1" />
            <div className="flex items-center gap-1 w-14 justify-end">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M1 9l2 2c4.97-4.97 13.03-4.97 18 0l2-2C16.93 2.93 7.08 2.93 1 9zm8 8l3 3 3-3c-1.65-1.66-4.34-1.66-6 0zm-4-4l2 2c2.76-2.76 7.24-2.76 10 0l2-2C15.14 9.14 8.87 9.14 5 13z"/>
              </svg>
              <div className="flex items-center">
                <div className="w-6 h-3 border border-current rounded-sm relative">
                  <div className="absolute inset-0.5 rounded-[1px]" style={{ backgroundColor: '#22c55e', width: '70%' }} />
                </div>
                <div className="w-0.5 h-1.5 rounded-r-sm ml-px" style={{ backgroundColor: 'currentColor' }} />
              </div>
            </div>
          </div>
          {type === 'sms' && (
            <div className="px-4 py-3" style={{ backgroundColor: '#f3f4f6', borderBottom: '1px solid #e5e7eb' }}>
              <div className="flex items-center gap-3">
                <svg className="w-5 h-5" style={{ color: '#3b82f6' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                <div className="flex-1 text-center">
                  <div className="w-10 h-10 rounded-full mx-auto flex items-center justify-center" style={{ backgroundColor: '#d1d5db' }}>
                    <span className="text-sm font-semibold" style={{ color: '#4b5563' }}>{initials}</span>
                  </div>
                  <p className="text-xs mt-1" style={{ color: '#6b7280' }}>{practiceName}</p>
                </div>
                <svg className="w-5 h-5" style={{ color: '#3b82f6' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
          )}
          <div style={{ backgroundColor: '#fff', minHeight: type === 'sms' ? '280px' : '400px', maxHeight: type === 'sms' ? '280px' : '400px', overflowY: 'auto' }}>
            {children}
          </div>
          <div className="px-4 py-4" style={{ backgroundColor: '#fff' }}>
            <div className="w-32 h-1 rounded-full mx-auto" style={{ backgroundColor: '#1f2937' }} />
          </div>
        </div>
      </div>
    </div>
  );

  // SMS Content for Mobile
  const SMSContent = () => (
    <div className="p-4 pt-6 h-full" style={{ background: 'linear-gradient(to bottom, #f9fafb, #ffffff)' }}>
      <p className="text-xs text-center mb-4" style={{ color: '#9ca3af' }}>Today</p>
      <div className="flex justify-start mb-2">
        <div className="max-w-[85%]">
          <div className="rounded-2xl rounded-bl-md px-4 py-2.5 text-sm leading-relaxed" style={{ backgroundColor: '#e5e7eb', color: '#111827' }}>
            {smsMessage}
          </div>
          <p className="text-[10px] mt-1 ml-2" style={{ color: '#9ca3af' }}>
            {new Date().toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })} · {smsMessage.length} chars
          </p>
        </div>
      </div>
    </div>
  );

  // Email Content for Mobile (Gmail-style)
  const MobileEmailContent = () => (
    <div className="h-full flex flex-col" style={{ backgroundColor: '#fff' }}>
      <div className="flex items-center justify-between px-3 py-2" style={{ borderBottom: '1px solid #f3f4f6' }}>
        <svg className="w-5 h-5" style={{ color: '#4b5563' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        <div className="flex items-center gap-3">
          <svg className="w-4 h-4" style={{ color: '#6b7280' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
          </svg>
          <svg className="w-4 h-4" style={{ color: '#6b7280' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
          <svg className="w-4 h-4" style={{ color: '#6b7280' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
        </div>
      </div>
      <div className="px-3 py-2" style={{ borderBottom: '1px solid #f3f4f6' }}>
        <div className="flex items-center gap-2">
          <h3 className="text-base font-normal flex-1 truncate" style={{ color: '#111827' }}>{subjectLine}</h3>
          <span className="text-[10px] px-1.5 py-0.5 rounded" style={{ backgroundColor: '#e5e7eb', color: '#4b5563' }}>Inbox</span>
        </div>
      </div>
      <div className="px-3 py-2 text-left" style={{ borderBottom: '1px solid #f3f4f6' }}>
        <div className="flex items-start gap-2">
          <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'linear-gradient(to bottom right, #60a5fa, #2563eb)' }}>
            <span className="text-xs font-bold" style={{ color: '#fff' }}>{initials}</span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-left" style={{ color: '#111827' }}>{practiceName} <span className="text-xs font-normal" style={{ color: '#6b7280' }}>{dateStr}</span></p>
            <p className="text-xs text-left" style={{ color: '#6b7280' }}>to me ▾</p>
          </div>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto px-3 py-3 text-left" style={{ backgroundColor: '#fff', colorScheme: 'light only', color: '#111827' }}>
        {emailHtml && notificationTemplate ? (
          <div className="overflow-hidden text-left" style={{ transform: 'scale(0.42)', transformOrigin: 'top left', width: '238%', colorScheme: 'light only', pointerEvents: 'none' }} dangerouslySetInnerHTML={{ __html: emailHtml }} />
        ) : (
          <p className="text-sm leading-relaxed whitespace-pre-wrap text-left" style={{ color: '#374151' }}>{emailMessage}</p>
        )}
      </div>
    </div>
  );

  // Desktop Email Preview
  const DesktopEmailPreview = () => (
    <div className="w-full max-w-3xl mx-auto rounded-lg shadow-xl overflow-hidden" style={{ backgroundColor: '#fff' }}>
      {/* Email client header */}
      <div className="px-4 py-3 flex items-center gap-3" style={{ backgroundColor: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
        <div className="flex gap-1.5">
          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#ef4444' }} />
          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#f59e0b' }} />
          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#22c55e' }} />
        </div>
        <div className="flex-1 flex justify-center">
          <div className="px-4 py-1 rounded-md text-xs" style={{ backgroundColor: '#e5e7eb', color: '#6b7280' }}>
            mail.google.com
          </div>
        </div>
      </div>
      
      {/* Email header */}
      <div className="px-6 py-4" style={{ borderBottom: '1px solid #e5e7eb' }}>
        <div className="flex items-center gap-4 mb-3">
          <div className="p-2 rounded-full" style={{ color: '#6b7280' }}>
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
          </div>
          <h2 className="text-xl font-normal flex-1" style={{ color: '#111827' }}>{subjectLine}</h2>
          <span className="px-2 py-1 rounded text-xs" style={{ backgroundColor: '#e5e7eb', color: '#4b5563' }}>Inbox</span>
        </div>
        <div className="flex items-start gap-3 ml-12">
          <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'linear-gradient(to bottom right, #60a5fa, #2563eb)' }}>
            <span className="text-sm font-bold" style={{ color: '#fff' }}>{initials}</span>
          </div>
          <div className="flex-1">
            <div className="flex items-baseline gap-2">
              <span className="font-medium" style={{ color: '#111827' }}>{practiceName}</span>
              <span className="text-sm" style={{ color: '#6b7280' }}>&lt;noreply@{practiceName.toLowerCase().replace(/\s+/g, '')}.com&gt;</span>
            </div>
            <p className="text-sm" style={{ color: '#6b7280' }}>to me</p>
          </div>
          <span className="text-sm" style={{ color: '#6b7280' }}>{dateStr}</span>
        </div>
      </div>
      
      {/* Email body */}
      <div className="p-6" style={{ backgroundColor: '#fff', colorScheme: 'light only', minHeight: '400px', maxHeight: '500px', overflowY: 'auto' }}>
        {emailHtml && notificationTemplate ? (
          <div className="text-left" style={{ colorScheme: 'light only', pointerEvents: 'none' }} dangerouslySetInnerHTML={{ __html: emailHtml }} />
        ) : (
          <p className="text-base leading-relaxed whitespace-pre-wrap" style={{ color: '#374151' }}>{emailMessage}</p>
        )}
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 py-8">
        {/* Backdrop */}
        <div className="fixed inset-0 transition-opacity" style={{ backgroundColor: 'rgba(0, 0, 0, 0.6)' }} onClick={onClose} />

        {/* Modal Container - supports dark mode */}
        <div className="relative z-10 w-full max-w-4xl rounded-xl shadow-2xl overflow-hidden bg-white dark:bg-dark-bg-secondary">
          {/* Modal Header - supports dark mode */}
          <div className="px-6 py-4 flex items-center justify-between border-b border-gray-200 dark:border-dark-border">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-dark-text-primary">{title}</h3>
            
            {/* View Mode Tabs */}
            <div className="flex items-center gap-1 p-1 rounded-lg bg-gray-100 dark:bg-dark-bg-tertiary">
              <button
                onClick={() => setViewMode('mobile')}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  viewMode === 'mobile' 
                    ? 'bg-white dark:bg-dark-bg-secondary shadow-sm text-gray-900 dark:text-dark-text-primary' 
                    : 'text-gray-500 dark:text-dark-text-muted hover:text-gray-700 dark:hover:text-dark-text-secondary'
                }`}
              >
                <Smartphone className="w-4 h-4" />
                Mobile
              </button>
              <button
                onClick={() => setViewMode('desktop')}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  viewMode === 'desktop' 
                    ? 'bg-white dark:bg-dark-bg-secondary shadow-sm text-gray-900 dark:text-dark-text-primary' 
                    : 'text-gray-500 dark:text-dark-text-muted hover:text-gray-700 dark:hover:text-dark-text-secondary'
                }`}
              >
                <Monitor className="w-4 h-4" />
                Desktop
              </button>
            </div>
            
            <button 
              onClick={onClose} 
              className="p-2 rounded-full transition-colors text-gray-500 dark:text-dark-text-muted hover:bg-gray-100 dark:hover:bg-dark-bg-tertiary"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Modal Body - light background for preview area */}
          <div className="p-6 bg-gray-50 dark:bg-dark-bg-primary" style={{ minHeight: '500px' }}>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: '#3b82f6' }} />
              </div>
            ) : viewMode === 'mobile' ? (
              <div className={`flex ${showEmail && showSms ? 'gap-8' : ''} items-start justify-center`}>
                {showSms && (
                  <div className="flex flex-col items-center">
                    <div className="mb-3 flex items-center gap-2 text-gray-500 dark:text-dark-text-muted">
                      <MessageSquare className="w-4 h-4" />
                      <span className="text-sm font-medium">SMS</span>
                    </div>
                    <IPhoneFrame type="sms">
                      <SMSContent />
                    </IPhoneFrame>
                  </div>
                )}
                {showEmail && (
                  <div className="flex flex-col items-center">
                    <div className="mb-3 flex items-center gap-2 text-gray-500 dark:text-dark-text-muted">
                      <Mail className="w-4 h-4" />
                      <span className="text-sm font-medium">Email</span>
                    </div>
                    <IPhoneFrame type="email">
                      <MobileEmailContent />
                    </IPhoneFrame>
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-6">
                {showSms && (
                  <div>
                    <div className="mb-3 flex items-center gap-2 text-gray-500 dark:text-dark-text-muted">
                      <MessageSquare className="w-4 h-4" />
                      <span className="text-sm font-medium">SMS Preview</span>
                    </div>
                    <div className="max-w-md mx-auto p-4 rounded-lg shadow-md" style={{ backgroundColor: '#fff' }}>
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#d1d5db' }}>
                          <span className="text-sm font-semibold" style={{ color: '#4b5563' }}>{initials}</span>
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-sm mb-1" style={{ color: '#111827' }}>{practiceName}</p>
                          <p className="text-sm leading-relaxed" style={{ color: '#374151' }}>{smsMessage}</p>
                          <p className="text-xs mt-2" style={{ color: '#9ca3af' }}>{smsMessage.length} characters</p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                {showEmail && <DesktopEmailPreview />}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// Helper to parse URL path for form mode
// ============================================================================

type FormMode = 'list' | 'new' | 'edit';
type FormType = 'reminder' | 'trigger' | 'overdue';

interface ParsedPath {
  mode: FormMode;
  type: FormType | null;
  id: string | null;
}

const parseSetAndForgetPath = (pathname: string): ParsedPath => {
  // /dashboard/set-and-forget/new/reminder
  // /dashboard/set-and-forget/new/trigger
  // /dashboard/set-and-forget/new/overdue
  // /dashboard/set-and-forget/edit/reminder/:id
  // /dashboard/set-and-forget/edit/trigger/:id
  // /dashboard/set-and-forget/edit/overdue/:id
  
  if (pathname.includes('/new/reminder')) {
    return { mode: 'new', type: 'reminder', id: null };
  }
  if (pathname.includes('/new/trigger')) {
    return { mode: 'new', type: 'trigger', id: null };
  }
  if (pathname.includes('/new/overdue')) {
    return { mode: 'new', type: 'overdue', id: null };
  }
  
  const editReminderMatch = pathname.match(/\/edit\/reminder\/([^/]+)/);
  if (editReminderMatch) {
    return { mode: 'edit', type: 'reminder', id: editReminderMatch[1] };
  }
  
  const editTriggerMatch = pathname.match(/\/edit\/trigger\/([^/]+)/);
  if (editTriggerMatch) {
    return { mode: 'edit', type: 'trigger', id: editTriggerMatch[1] };
  }
  
  const editOverdueMatch = pathname.match(/\/edit\/overdue\/([^/]+)/);
  if (editOverdueMatch) {
    return { mode: 'edit', type: 'overdue', id: editOverdueMatch[1] };
  }
  
  return { mode: 'list', type: null, id: null };
};

// Export for Dashboard breadcrumb use
export const getSetAndForgetBreadcrumbInfo = (pathname: string, searchParams: URLSearchParams) => {
  const parsed = parseSetAndForgetPath(pathname);
  
  // Infer tab from path if we're on a new/edit page
  let tab = searchParams.get('tab') || 'reminders';
  if (parsed.type === 'trigger') {
    tab = 'triggers';
  } else if (parsed.type === 'reminder') {
    tab = 'reminders';
  } else if (parsed.type === 'overdue') {
    tab = 'overdue';
  }
  
  return {
    ...parsed,
    activeTab: tab as TabKey,
  };
};

// ============================================================================
// Main Component
// ============================================================================

const SetAndForgetPage = forwardRef<SetAndForgetHandle>((_, ref) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = (searchParams.get('tab') as TabKey) || 'reminders';
  
  // Parse URL to determine if we're in create/edit mode
  const { mode, type, id } = parseSetAndForgetPath(location.pathname);
  const isFormMode = mode === 'new' || mode === 'edit';

  const setActiveTab = (tab: TabKey) => {
    setSearchParams({ tab });
  };

  // Expose openCreate method to parent (Dashboard header button)
  useImperativeHandle(ref, () => ({
    openCreate: () => {
      if (activeTab === 'triggers') {
        navigate('/dashboard/set-and-forget/new/trigger');
      } else if (activeTab === 'overdue') {
        navigate('/dashboard/set-and-forget/new/overdue');
      } else {
        navigate('/dashboard/set-and-forget/new/reminder');
      }
    }
  }));

  // If in form mode, show the dedicated form view
  if (isFormMode) {
    if (type === 'reminder') {
      return (
        <ReminderFormPage 
          mode={mode} 
          editId={id} 
          onClose={() => navigate('/dashboard/set-and-forget?tab=reminders')} 
        />
      );
    }
    if (type === 'trigger') {
      return (
        <TriggerFormPage 
          mode={mode} 
          editId={id} 
          onClose={() => navigate('/dashboard/set-and-forget?tab=triggers')} 
        />
      );
    }
    if (type === 'overdue') {
      return (
        <OverdueFormPage 
          mode={mode} 
          editId={id} 
          onClose={() => navigate('/dashboard/set-and-forget?tab=overdue')} 
        />
      );
    }
  }

  return (
    <div className="h-full flex flex-col">
      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-dark-border">
        <nav className="flex space-x-4 px-4" aria-label="Tabs">
          <button
            onClick={() => setActiveTab('reminders')}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'reminders'
                ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-gray-500 dark:text-dark-text-muted hover:text-gray-700 dark:hover:text-dark-text-primary hover:border-gray-300'
            }`}
          >
            <div className="flex items-center gap-2">
              <Bell className="w-4 h-4" />
              Appointment Reminders
            </div>
          </button>
          <button
            onClick={() => setActiveTab('triggers')}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'triggers'
                ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-gray-500 dark:text-dark-text-muted hover:text-gray-700 dark:hover:text-dark-text-primary hover:border-gray-300'
            }`}
          >
            <div className="flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Form Triggers
            </div>
          </button>
          <button
            onClick={() => setActiveTab('overdue')}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'overdue'
                ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-gray-500 dark:text-dark-text-muted hover:text-gray-700 dark:hover:text-dark-text-primary hover:border-gray-300'
            }`}
          >
            <div className="flex items-center gap-2">
              <AlertCircle className="w-4 h-4" />
              Overdue Vaccines
            </div>
          </button>
          <button
            onClick={() => setActiveTab('logs')}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'logs'
                ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-gray-500 dark:text-dark-text-muted hover:text-gray-700 dark:hover:text-dark-text-primary hover:border-gray-300'
            }`}
          >
            <div className="flex items-center gap-2">
              <ScrollText className="w-4 h-4" />
              Logs
            </div>
          </button>
        </nav>
      </div>

      {/* Tab Content - List view only */}
      <div className="flex-1 overflow-y-auto">
        {activeTab === 'reminders' && (
          <AppointmentRemindersTab />
        )}
        {activeTab === 'triggers' && (
          <FormTriggersTab />
        )}
        {activeTab === 'overdue' && (
          <OverdueVaccinesTab />
        )}
        {activeTab === 'logs' && (
          <OverdueVaccineLogsPage />
        )}
      </div>
    </div>
  );
});

SetAndForgetPage.displayName = 'SetAndForgetPage';

// ============================================================================
// Reminder Form Page (dedicated full-screen form)
// ============================================================================

interface FormPageProps {
  mode: 'new' | 'edit';
  editId: string | null;
  onClose: () => void;
}

const ReminderFormPage: React.FC<FormPageProps> = ({ mode, editId, onClose }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const urlReminderType = (searchParams.get('type') as ReminderType | null) || null;
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(mode === 'edit');
  const [error, setError] = useState<string | null>(null);
  
  // Templates state
  const [templates, setTemplates] = useState<NotificationTemplate[]>([]);
  const [templateDropdownOpen, setTemplateDropdownOpen] = useState(false);
  const templateDropdownRef = useRef<HTMLDivElement>(null);
  
  const [form, setForm] = useState<ReminderForm>({
    reminder_type: 'appointment',
    value: '1',
    unit: 'days',
    inactivity_value: '30',
    inactivity_unit: 'days',
    cadence_value: '1',
    cadence_unit: 'days',
    delivery_method: 'sms',
    is_enabled: true,
    message_template: '',
    template_id: null,
  });

  // Get templates filtered by current delivery method
  const filteredTemplates = templates.filter(t => t.channel === form.delivery_method);

  useEffect(() => {
    if (mode === 'edit' && editId) {
      loadSetting();
    }
    fetchTemplates();
  }, [mode, editId]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (templateDropdownRef.current && !templateDropdownRef.current.contains(e.target as Node)) {
        setTemplateDropdownOpen(false);
      }
    };
    if (templateDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [templateDropdownOpen]);

  const fetchTemplates = async () => {
    try {
      const templatesData = await getTemplates();
      setTemplates(templatesData);
    } catch (error) {
      console.error('Failed to fetch templates:', error);
    }
  };

  const loadSetting = async () => {
    if (!user?.practice_id || !editId) return;
    setLoading(true);
    try {
      const token = (localStorage.getItem('access_token') || localStorage.getItem('token'));
      const headers = { 'Authorization': `Bearer ${token}` };

      let appointmentSettings: AppointmentReminderSetting[] = [];
      let noVisitSettings: NoVisitReminderSetting[] = [];

      if (urlReminderType === 'appointment' || !urlReminderType) {
        const response = await fetch(
          API_ENDPOINTS.APPOINTMENT_REMINDER_SETTINGS.LIST(user.practice_id),
          { headers }
        );
        if (!response.ok) throw new Error('Failed to load appointment settings');
        appointmentSettings = await response.json();
      }

      if (urlReminderType === 'no_visit' || !urlReminderType) {
        const response = await fetch(
          API_ENDPOINTS.NO_VISIT_REMINDER_SETTINGS.LIST(user.practice_id),
          { headers }
        );
        if (!response.ok) throw new Error('Failed to load Appt Due settings');
        noVisitSettings = await response.json();
      }

      const appointmentSetting = appointmentSettings.find(s => s.id === editId);
      if (appointmentSetting) {
        const { value, unit } = convertFromMinutes(appointmentSetting.minutes_before);
        setForm({
          reminder_type: 'appointment',
          value: String(value),
          unit,
          inactivity_value: '30',
          inactivity_unit: 'days',
          cadence_value: '7',
          cadence_unit: 'days',
          delivery_method: normalizeDeliveryMethod(appointmentSetting.delivery_method),
          is_enabled: appointmentSetting.is_enabled,
          message_template: appointmentSetting.message_template || '',
          template_id: appointmentSetting.template_id || null,
        });
        return;
      }

      const noVisitSetting = noVisitSettings.find(s => s.id === editId);
      if (noVisitSetting) {
        const inactivity = convertFromMinutes(noVisitSetting.inactivity_minutes);
        const cadence = convertFromMinutes(noVisitSetting.cadence_minutes);
        setForm({
          reminder_type: 'no_visit',
          value: '1',
          unit: 'days',
          inactivity_value: String(inactivity.value),
          inactivity_unit: inactivity.unit,
          cadence_value: String(cadence.value),
          cadence_unit: cadence.unit,
          delivery_method: normalizeDeliveryMethod(noVisitSetting.delivery_method),
          is_enabled: noVisitSetting.is_enabled,
          message_template: noVisitSetting.message_template || '',
          template_id: noVisitSetting.template_id || null,
        });
        return;
      }

      throw new Error('Reminder not found');
    } catch (err) {
      setError('Failed to load reminder');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user?.practice_id) return;

    setSaving(true);
    setError(null);

    try {
      const token = (localStorage.getItem('access_token') || localStorage.getItem('token'));
      const isAppointment = form.reminder_type === 'appointment';
      let payload: Record<string, unknown> = {
        delivery_method: toBackendDeliveryMethod(form.delivery_method),
        is_enabled: form.is_enabled,
        message_template: form.message_template || undefined,
        template_id: form.template_id || undefined,
      };

      if (isAppointment) {
        const minutes = convertToMinutes(Number(form.value), form.unit);
        payload = {
          ...payload,
          minutes_before: minutes,
          from_email_address_id: undefined,
        };
      } else {
        const inactivityMinutes = convertToMinutes(Number(form.inactivity_value), form.inactivity_unit);
        const cadenceMinutes = 1440;
        payload = {
          ...payload,
          inactivity_minutes: inactivityMinutes,
          cadence_minutes: cadenceMinutes,
        };
      }

      if (mode === 'new') {
        payload.practice_id = user.practice_id;
      }

      const url = mode === 'edit' && editId
        ? (isAppointment
          ? API_ENDPOINTS.APPOINTMENT_REMINDER_SETTINGS.UPDATE(editId)
          : API_ENDPOINTS.NO_VISIT_REMINDER_SETTINGS.UPDATE(editId))
        : (isAppointment
          ? API_ENDPOINTS.APPOINTMENT_REMINDER_SETTINGS.CREATE
          : API_ENDPOINTS.NO_VISIT_REMINDER_SETTINGS.CREATE);

      const response = await fetch(url, {
        method: mode === 'edit' ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) throw new Error('Failed to save setting');
      onClose();
    } catch (err) {
      setError('Failed to save reminder setting');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-2xl mx-auto">
      <form onSubmit={handleSubmit} className="bg-white dark:bg-dark-bg-secondary border border-gray-200 dark:border-dark-border rounded-lg p-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-dark-text-primary mb-6">
          {mode === 'edit'
            ? 'Edit Reminder'
            : form.reminder_type === 'no_visit'
              ? 'New Needs Appointment Reminder'
              : 'New Appointment Reminder'}
        </h2>

        {error && (
          <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center gap-2 text-red-700 dark:text-red-400">
            <AlertCircle className="w-4 h-4" />
            {error}
          </div>
        )}
        
        {/* Reminder Type */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-2">
            Reminder Type
          </label>
          <select
            value={form.reminder_type}
            onChange={(e) => {
              const reminderType = e.target.value as ReminderType;
              setForm({ ...form, reminder_type: reminderType, template_id: null });
            }}
            className="w-full px-3 py-2 border border-gray-300 dark:border-dark-border dark:bg-dark-bg-secondary dark:text-dark-text-primary text-sm focus:ring-blue-500 focus:border-blue-500 rounded"
          >
            <option value="appointment">Before appointment</option>
            <option value="no_visit">Needs appointment</option>
          </select>
        </div>

        {/* Time setting */}
        {form.reminder_type === 'appointment' ? (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-2">
              Send notification
            </label>
            <div className="flex items-center gap-2">
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                value={form.value}
                onChange={(e) => {
                  const val = e.target.value.replace(/[^0-9]/g, '');
                  setForm({ ...form, value: val });
                }}
                onBlur={() => {
                  const numValue = parseInt(form.value) || 1;
                  setForm({ ...form, value: String(Math.max(1, numValue)) });
                }}
                className="w-20 px-3 py-2 border border-gray-300 dark:border-dark-border dark:bg-dark-bg-secondary dark:text-dark-text-primary text-sm focus:ring-blue-500 focus:border-blue-500 rounded"
              />
              <select
                value={form.unit}
                onChange={(e) => setForm({ ...form, unit: e.target.value as TimeUnit })}
                className="px-3 py-2 border border-gray-300 dark:border-dark-border dark:bg-dark-bg-secondary dark:text-dark-text-primary text-sm focus:ring-blue-500 focus:border-blue-500 rounded"
              >
                <option value="minutes">minutes</option>
                <option value="hours">hours</option>
                <option value="days">days</option>
              </select>
              <span className="text-gray-600 dark:text-dark-text-muted text-sm">before appointment</span>
            </div>
          </div>
        ) : (
          <>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-2">
                Has not visited in
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={form.inactivity_value}
                  onChange={(e) => {
                    const val = e.target.value.replace(/[^0-9]/g, '');
                    setForm({ ...form, inactivity_value: val });
                  }}
                  onBlur={() => {
                    const numValue = parseInt(form.inactivity_value) || 1;
                    setForm({ ...form, inactivity_value: String(Math.max(1, numValue)) });
                  }}
                  className="w-20 px-3 py-2 border border-gray-300 dark:border-dark-border dark:bg-dark-bg-secondary dark:text-dark-text-primary text-sm focus:ring-blue-500 focus:border-blue-500 rounded"
                />
                <select
                  value={form.inactivity_unit}
                  onChange={(e) => setForm({ ...form, inactivity_unit: e.target.value as TimeUnit })}
                  className="px-3 py-2 border border-gray-300 dark:border-dark-border dark:bg-dark-bg-secondary dark:text-dark-text-primary text-sm focus:ring-blue-500 focus:border-blue-500 rounded"
                >
                  <option value="minutes">minutes</option>
                  <option value="hours">hours</option>
                  <option value="days">days</option>
                </select>
              </div>
            </div>
            <div className="mb-4">
              <p className="text-sm text-gray-600 dark:text-dark-text-muted">
                Check runs daily.
              </p>
            </div>
          </>
        )}

        {/* Message Type */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-2">
            Message Type
          </label>
          <div className="flex gap-3">
            {(['sms', 'email_plain', 'email_html'] as SetAndForgetDeliveryMethod[]).map((method) => {
              const isSelected = form.delivery_method === method;
              const colorClass = method === 'sms' 
                ? (isSelected ? 'border-green-500 bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-400' : '')
                : method === 'email_plain'
                ? (isSelected ? 'border-orange-500 bg-orange-50 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400' : '')
                : (isSelected ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400' : '');
              
              return (
                <button
                  key={method}
                  type="button"
                  onClick={() => setForm({ ...form, delivery_method: method, template_id: null })}
                  className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                    isSelected
                      ? colorClass
                      : 'border-gray-300 dark:border-dark-border hover:bg-gray-50 dark:hover:bg-dark-bg-secondary text-gray-700 dark:text-dark-text-primary'
                  }`}
                >
                  {getDeliveryIcon(method)}
                  <span className="text-sm font-medium">
                    {getDeliveryLabel(method)}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Template Selection */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-2">
            Use Template
          </label>
          {filteredTemplates.length > 0 ? (
            <div ref={templateDropdownRef} className="relative">
              <button
                type="button"
                onClick={() => setTemplateDropdownOpen(!templateDropdownOpen)}
                className="w-full flex items-center justify-between px-3 py-2 bg-white dark:bg-slate-800 border border-gray-300 dark:border-slate-700 rounded-lg text-sm text-left hover:border-gray-400 dark:hover:border-slate-600 transition-colors"
              >
                <span className={form.template_id ? 'text-gray-900 dark:text-slate-200' : 'text-gray-500 dark:text-slate-400'}>
                  {form.template_id
                    ? templates.find(t => t.id === form.template_id)?.name || 'Select a template'
                    : 'Select a template'}
                </span>
                <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${templateDropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              
              {templateDropdownOpen && (
                <div className="absolute z-50 w-full mt-1 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg shadow-lg overflow-hidden">
                  <div className="max-h-60 overflow-y-auto">
                    {filteredTemplates.map((template) => (
                      <button
                        key={template.id}
                        type="button"
                        onClick={() => {
                          setForm({ ...form, template_id: template.id });
                          setTemplateDropdownOpen(false);
                        }}
                        className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left transition-colors ${
                          form.template_id === template.id 
                            ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400' 
                            : 'text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-700'
                        }`}
                      >
                        {form.template_id === template.id && <Check className="w-4 h-4" />}
                        <span className={form.template_id === template.id ? '' : 'ml-6'}>{template.name}</span>
                      </button>
                    ))}
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setTemplateDropdownOpen(false);
                      navigate(`/dashboard/templates/new?channel=${form.delivery_method}`);
                    }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 border-t border-gray-200 dark:border-dark-border"
                  >
                    <Plus className="w-4 h-4" />
                    Create {getDeliveryLabel(form.delivery_method)} Template
                  </button>
                </div>
              )}
            </div>
          ) : (
            <button
              type="button"
              onClick={() => navigate(`/dashboard/templates/new?channel=${form.delivery_method}`)}
              className="w-full flex items-center justify-center gap-2 px-3 py-2 border-2 border-dashed border-gray-300 dark:border-dark-border rounded-lg text-sm text-gray-600 dark:text-dark-text-muted hover:border-blue-400 hover:text-blue-600 hover:bg-blue-50/50 dark:hover:bg-blue-900/20 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Create {getDeliveryLabel(form.delivery_method)} Template
            </button>
          )}
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-dark-border">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-dark-text-primary bg-white dark:bg-dark-bg-secondary border border-gray-300 dark:border-dark-border hover:bg-gray-50 dark:hover:bg-dark-bg-tertiary rounded"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving || !form.template_id}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 rounded"
          >
            {saving ? 'Saving...' : mode === 'edit' ? 'Update Reminder' : 'Create Reminder'}
          </button>
        </div>
      </form>
    </div>
  );
};

// ============================================================================
// Trigger Form Page (dedicated full-screen form)
// ============================================================================

const TriggerFormPage: React.FC<FormPageProps> = ({ mode, editId, onClose }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [formTemplates, setFormTemplates] = useState<FormTemplate[]>([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Notification templates state
  const [notificationTemplates, setNotificationTemplates] = useState<NotificationTemplate[]>([]);
  const [templateDropdownOpen, setTemplateDropdownOpen] = useState(false);
  const templateDropdownRef = useRef<HTMLDivElement>(null);
  
  const [form, setForm] = useState<TriggerForm>({
    value: '3',
    unit: 'days',
    delivery_method: 'sms',
    is_enabled: true,
    message_template: '',
    form_template_id: '',
    template_id: null,
  });

  // Get notification templates filtered by current delivery method
  const filteredNotificationTemplates = notificationTemplates.filter(t => t.channel === form.delivery_method);

  useEffect(() => {
    loadData();
    fetchNotificationTemplates();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (templateDropdownRef.current && !templateDropdownRef.current.contains(e.target as Node)) {
        setTemplateDropdownOpen(false);
      }
    };
    if (templateDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [templateDropdownOpen]);

  const fetchNotificationTemplates = async () => {
    try {
      const templatesData = await getTemplates();
      setNotificationTemplates(templatesData);
    } catch (error) {
      console.error('Failed to fetch notification templates:', error);
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const templatesData = await getFormTemplates(true);
      setFormTemplates(templatesData);

      // If editing, find and load the trigger
      if (mode === 'edit' && editId) {
        for (const template of templatesData) {
          const triggers = await getFormTriggers(template.id);
          const trigger = triggers.find(t => t.id === editId);
          if (trigger) {
            const { value, unit } = convertFromMinutes(trigger.minutes_before);
            setForm({
              value: String(value),
              unit,
              delivery_method: normalizeDeliveryMethod(trigger.delivery_method),
              is_enabled: trigger.is_enabled,
              message_template: trigger.message_template || '',
              form_template_id: template.id,
              template_id: trigger.template_id || null,
            });
            break;
          }
        }
      }
    } catch (err) {
      setError('Failed to load data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.form_template_id) {
      setError('Please select a form template');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const minutes = convertToMinutes(Number(form.value), form.unit);
      const payload = {
        minutes_before: minutes,
        delivery_method: toBackendDeliveryMethod(form.delivery_method as SetAndForgetDeliveryMethod),
        is_enabled: form.is_enabled,
        message_template: form.message_template || undefined,
        template_id: form.template_id || undefined,
      };

      if (mode === 'edit' && editId) {
        await updateFormTrigger(editId, payload);
      } else {
        await createFormTrigger(form.form_template_id, payload);
      }

      onClose();
    } catch (err) {
      setError('Failed to save trigger');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-2xl mx-auto">
      <form onSubmit={handleSubmit} className="bg-white dark:bg-dark-bg-secondary border border-gray-200 dark:border-dark-border rounded-lg p-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-dark-text-primary mb-6">
          {mode === 'edit' ? 'Edit Trigger' : 'New Form Trigger'}
        </h2>

        {error && (
          <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center gap-2 text-red-700 dark:text-red-400">
            <AlertCircle className="w-4 h-4" />
            {error}
          </div>
        )}

        {/* Form Template Selection */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-2">
            Form template
          </label>
          <select
            value={form.form_template_id}
            onChange={(e) => setForm({ ...form, form_template_id: e.target.value })}
            disabled={mode === 'edit'}
            className="w-full px-3 py-2 border border-gray-300 dark:border-dark-border dark:bg-dark-bg-secondary dark:text-dark-text-primary text-sm focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 rounded"
          >
            <option value="">Select a form...</option>
            {formTemplates.map((t) => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
        </div>
        
        {/* Time setting */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-2">
            Send notification
          </label>
          <div className="flex items-center gap-2">
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              value={form.value}
              onChange={(e) => {
                const val = e.target.value.replace(/[^0-9]/g, '');
                setForm({ ...form, value: val });
              }}
              onBlur={() => {
                const numValue = parseInt(form.value) || 1;
                setForm({ ...form, value: String(Math.max(1, numValue)) });
              }}
              className="w-20 px-3 py-2 border border-gray-300 dark:border-dark-border dark:bg-dark-bg-secondary dark:text-dark-text-primary text-sm focus:ring-blue-500 focus:border-blue-500 rounded"
            />
            <select
              value={form.unit}
              onChange={(e) => setForm({ ...form, unit: e.target.value as TimeUnit })}
              className="px-3 py-2 border border-gray-300 dark:border-dark-border dark:bg-dark-bg-secondary dark:text-dark-text-primary text-sm focus:ring-blue-500 focus:border-blue-500 rounded"
            >
              <option value="minutes">minutes</option>
              <option value="hours">hours</option>
              <option value="days">days</option>
            </select>
            <span className="text-gray-600 dark:text-dark-text-muted text-sm">before appointment</span>
          </div>
        </div>

        {/* Message Type */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-2">
            Message Type
          </label>
          <div className="flex gap-3">
            {(['sms', 'email_plain', 'email_html'] as SetAndForgetDeliveryMethod[]).map((method) => {
              const isSelected = form.delivery_method === method;
              const colorClass = method === 'sms' 
                ? (isSelected ? 'border-green-500 bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-400' : '')
                : method === 'email_plain'
                ? (isSelected ? 'border-orange-500 bg-orange-50 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400' : '')
                : (isSelected ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400' : '');
              
              return (
                <button
                  key={method}
                  type="button"
                  onClick={() => setForm({ ...form, delivery_method: method, template_id: null })}
                  className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                    isSelected
                      ? colorClass
                      : 'border-gray-300 dark:border-dark-border hover:bg-gray-50 dark:hover:bg-dark-bg-secondary text-gray-700 dark:text-dark-text-primary'
                  }`}
                >
                  {getDeliveryIcon(method)}
                  <span className="text-sm font-medium">
                    {getDeliveryLabel(method)}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Template Selection */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-2">
            Use Template
          </label>
          {filteredNotificationTemplates.length > 0 ? (
            <div ref={templateDropdownRef} className="relative">
              <button
                type="button"
                onClick={() => setTemplateDropdownOpen(!templateDropdownOpen)}
                className="w-full flex items-center justify-between px-3 py-2 bg-white dark:bg-slate-800 border border-gray-300 dark:border-slate-700 rounded-lg text-sm text-left hover:border-gray-400 dark:hover:border-slate-600 transition-colors"
              >
                <span className={form.template_id ? 'text-gray-900 dark:text-slate-200' : 'text-gray-500 dark:text-slate-400'}>
                  {form.template_id
                    ? notificationTemplates.find(t => t.id === form.template_id)?.name || 'Select a template'
                    : 'Select a template'}
                </span>
                <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${templateDropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              
              {templateDropdownOpen && (
                <div className="absolute z-50 w-full mt-1 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg shadow-lg overflow-hidden">
                  <div className="max-h-60 overflow-y-auto">
                    {filteredNotificationTemplates.map((template) => (
                      <button
                        key={template.id}
                        type="button"
                        onClick={() => {
                          setForm({ ...form, template_id: template.id });
                          setTemplateDropdownOpen(false);
                        }}
                        className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left transition-colors ${
                          form.template_id === template.id 
                            ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400' 
                            : 'text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-700'
                        }`}
                      >
                        {form.template_id === template.id && <Check className="w-4 h-4" />}
                        <span className={form.template_id === template.id ? '' : 'ml-6'}>{template.name}</span>
                      </button>
                    ))}
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setTemplateDropdownOpen(false);
                      navigate(`/dashboard/templates/new?channel=${form.delivery_method}`);
                    }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 border-t border-gray-200 dark:border-dark-border"
                  >
                    <Plus className="w-4 h-4" />
                    Create {getDeliveryLabel(form.delivery_method as SetAndForgetDeliveryMethod)} Template
                  </button>
                </div>
              )}
            </div>
          ) : (
            <button
              type="button"
              onClick={() => navigate(`/dashboard/templates/new?channel=${form.delivery_method}`)}
              className="w-full flex items-center justify-center gap-2 px-3 py-2 border-2 border-dashed border-gray-300 dark:border-dark-border rounded-lg text-sm text-gray-600 dark:text-dark-text-muted hover:border-blue-400 hover:text-blue-600 hover:bg-blue-50/50 dark:hover:bg-blue-900/20 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Create {getDeliveryLabel(form.delivery_method as SetAndForgetDeliveryMethod)} Template
            </button>
          )}
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-dark-border">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-dark-text-primary bg-white dark:bg-dark-bg-secondary border border-gray-300 dark:border-dark-border hover:bg-gray-50 dark:hover:bg-dark-bg-tertiary rounded"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving || !form.template_id || !form.form_template_id}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 rounded"
          >
            {saving ? 'Saving...' : mode === 'edit' ? 'Update Trigger' : 'Create Trigger'}
          </button>
        </div>
      </form>
    </div>
  );
};

// ============================================================================
// Overdue Vaccine Form Page
// ============================================================================

interface OverdueFormState {
  value: string;
  unit: TimeUnit;
  delivery_method: SetAndForgetDeliveryMethod;
  template_id: string | null;
}

const OverdueFormPage: React.FC<FormPageProps> = ({ mode, editId, onClose }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(mode === 'edit');
  const [error, setError] = useState<string | null>(null);
  
  // Templates state
  const [templates, setTemplates] = useState<NotificationTemplate[]>([]);
  const [templateDropdownOpen, setTemplateDropdownOpen] = useState(false);
  const templateDropdownRef = useRef<HTMLDivElement>(null);
  
  const [form, setForm] = useState<OverdueFormState>({
    value: '0',
    unit: 'minutes',
    delivery_method: 'email_html',
    template_id: null,
  });

  // Get templates filtered by current delivery method
  const filteredTemplates = templates.filter(t => t.channel === form.delivery_method);

  useEffect(() => {
    if (mode === 'edit' && editId) {
      loadTrigger();
    }
    fetchTemplates();
  }, [mode, editId]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (templateDropdownRef.current && !templateDropdownRef.current.contains(e.target as Node)) {
        setTemplateDropdownOpen(false);
      }
    };
    if (templateDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [templateDropdownOpen]);

  const fetchTemplates = async () => {
    try {
      const templatesData = await getTemplates();
      setTemplates(templatesData);
    } catch (error) {
      console.error('Failed to fetch templates:', error);
    }
  };

  const loadTrigger = async () => {
    if (!editId) return;
    
    setLoading(true);
    try {
      const token = (localStorage.getItem('access_token') || localStorage.getItem('token'));
      const response = await fetch(`${API_BASE_URL}/api/v1/overdue-vaccine-triggers`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      
      if (!response.ok) throw new Error('Failed to load triggers');
      
      const triggers = await response.json();
      const trigger = triggers.find((t: OverdueVaccineTrigger) => t.id === editId);
      
      if (trigger) {
        // Convert from minutes stored in days_overdue
        const { value, unit } = convertFromMinutes(trigger.days_overdue);
        setForm({
          value: String(value),
          unit,
          delivery_method: normalizeDeliveryMethod(trigger.delivery_method),
          template_id: trigger.template_id,
        });
      }
    } catch (err) {
      setError('Failed to load trigger');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      const numValue = Number(form.value);
      
      if (isNaN(numValue) || numValue < 0) {
        setError('Please enter a valid number');
        setSaving(false);
        return;
      }

      // Convert to minutes (stored in days_overdue field for now)
      const minutesOverdue = convertToMinutes(numValue, form.unit);

      const payload = {
        days_overdue: minutesOverdue, // Actually stores minutes now
        delivery_method: toBackendDeliveryMethod(form.delivery_method),
        is_enabled: true, // Always active
        is_annual_summary: false,
        message_template: null,
        template_id: form.template_id,
      };

      const url = mode === 'edit' && editId
        ? `${API_BASE_URL}/api/v1/overdue-vaccine-triggers/${editId}`
        : `${API_BASE_URL}/api/v1/overdue-vaccine-triggers`;
      
      const method = mode === 'edit' ? 'PUT' : 'POST';

      const token = (localStorage.getItem('access_token') || localStorage.getItem('token'));
      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to save trigger');
      }

      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save trigger');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-2xl mx-auto">
      <form onSubmit={handleSubmit} className="bg-white dark:bg-dark-bg-secondary border border-gray-200 dark:border-dark-border rounded-lg p-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-dark-text-primary mb-6">
          {mode === 'edit' ? 'Edit Overdue Reminder' : 'New Overdue Vaccine Reminder'}
        </h2>

        {error && (
          <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center gap-2 text-red-700 dark:text-red-400">
            <AlertCircle className="w-4 h-4" />
            {error}
          </div>
        )}
        
        {/* Time setting */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-2">
            Send notification
          </label>
          <div className="flex items-center gap-2">
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              value={form.value}
              onChange={(e) => {
                const val = e.target.value.replace(/[^0-9]/g, '');
                setForm({ ...form, value: val });
              }}
              onBlur={() => {
                const numValue = parseInt(form.value) || 0;
                setForm({ ...form, value: String(Math.max(0, numValue)) });
              }}
              className="w-20 px-3 py-2 border border-gray-300 dark:border-dark-border dark:bg-dark-bg-secondary dark:text-dark-text-primary text-sm focus:ring-blue-500 focus:border-blue-500 rounded"
            />
            <select
              value={form.unit}
              onChange={(e) => setForm({ ...form, unit: e.target.value as TimeUnit })}
              className="px-3 py-2 border border-gray-300 dark:border-dark-border dark:bg-dark-bg-secondary dark:text-dark-text-primary text-sm focus:ring-blue-500 focus:border-blue-500 rounded"
            >
              <option value="minutes">minutes</option>
              <option value="hours">hours</option>
              <option value="days">days</option>
            </select>
            <span className="text-gray-600 dark:text-dark-text-muted text-sm">after becoming overdue</span>
          </div>
          <p className="mt-1 text-xs text-gray-500 dark:text-dark-text-muted">
            Enter 0 to send immediately when a vaccine becomes overdue (3 min delay for EventBridge)
          </p>
        </div>

        {/* Message Type */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-2">
            Message Type
          </label>
          <div className="flex gap-3">
            {(['sms', 'email_plain', 'email_html'] as SetAndForgetDeliveryMethod[]).map((method) => {
              const isSelected = form.delivery_method === method;
              const colorClass = method === 'sms' 
                ? (isSelected ? 'border-green-500 bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-400' : '')
                : method === 'email_plain'
                ? (isSelected ? 'border-orange-500 bg-orange-50 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400' : '')
                : (isSelected ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400' : '');
              
              return (
                <button
                  key={method}
                  type="button"
                  onClick={() => setForm({ ...form, delivery_method: method, template_id: null })}
                  className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                    isSelected
                      ? colorClass
                      : 'border-gray-300 dark:border-dark-border hover:bg-gray-50 dark:hover:bg-dark-bg-secondary text-gray-700 dark:text-dark-text-primary'
                  }`}
                >
                  {getDeliveryIcon(method)}
                  <span className="text-sm font-medium">
                    {getDeliveryLabel(method)}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Template Selection */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-2">
            Use Template
          </label>
          {filteredTemplates.length > 0 ? (
            <div ref={templateDropdownRef} className="relative">
              <button
                type="button"
                onClick={() => setTemplateDropdownOpen(!templateDropdownOpen)}
                className="w-full flex items-center justify-between px-3 py-2 bg-white dark:bg-dark-bg-secondary border border-gray-300 dark:border-dark-border rounded-lg text-sm text-left hover:border-gray-400 dark:hover:border-dark-border-hover transition-colors"
              >
                <span className={form.template_id ? 'text-gray-900 dark:text-dark-text-primary' : 'text-gray-500 dark:text-dark-text-muted'}>
                  {form.template_id
                    ? filteredTemplates.find(t => t.id === form.template_id)?.name || 'Select a template'
                    : 'Select a template'}
                </span>
                <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${templateDropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              
              {templateDropdownOpen && (
                <div className="absolute z-50 w-full mt-1 bg-white dark:bg-dark-bg-secondary border border-gray-200 dark:border-dark-border rounded-lg shadow-lg overflow-hidden">
                  <div className="max-h-60 overflow-y-auto">
                    {filteredTemplates.map((template) => (
                      <button
                        key={template.id}
                        type="button"
                        onClick={() => {
                          setForm({ ...form, template_id: template.id });
                          setTemplateDropdownOpen(false);
                        }}
                        className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left transition-colors ${
                          form.template_id === template.id 
                            ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400' 
                            : 'text-gray-700 dark:text-dark-text-secondary hover:bg-gray-50 dark:hover:bg-dark-bg-tertiary'
                        }`}
                      >
                        {form.template_id === template.id && <Check className="w-4 h-4" />}
                        <span className={form.template_id === template.id ? '' : 'ml-6'}>{template.name}</span>
                      </button>
                    ))}
                  </div>
                  {/* Create Template Button */}
                  <button
                    type="button"
                    onClick={() => {
                      setTemplateDropdownOpen(false);
                      navigate(`/dashboard/templates/new?channel=${form.delivery_method}`);
                    }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 border-t border-gray-200 dark:border-dark-border"
                  >
                    <Plus className="w-4 h-4" />
                    Create {getDeliveryLabel(form.delivery_method)} Template
                  </button>
                </div>
              )}
            </div>
          ) : (
            <button
              type="button"
              onClick={() => navigate(`/dashboard/templates/new?channel=${form.delivery_method}`)}
              className="w-full flex items-center justify-center gap-2 px-3 py-2 border-2 border-dashed border-gray-300 dark:border-dark-border rounded-lg text-sm text-gray-600 dark:text-dark-text-muted hover:border-blue-400 hover:text-blue-600 hover:bg-blue-50/50 dark:hover:bg-blue-900/20 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Create {getDeliveryLabel(form.delivery_method)} Template
            </button>
          )}
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-dark-border">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-600 dark:text-dark-text-muted hover:text-gray-800 dark:hover:text-dark-text-primary"
            disabled={saving}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Saving...
              </>
            ) : (
              <>
                <Check className="w-4 h-4" />
                {mode === 'edit' ? 'Save Changes' : 'Create Reminder'}
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

// ============================================================================
// Appointment Reminders Tab (list view only)
// ============================================================================

type ReminderSettingListItem =
  | (AppointmentReminderSetting & { reminder_type: 'appointment' })
  | (NoVisitReminderSetting & { reminder_type: 'no_visit' });

const AppointmentRemindersTab: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [settings, setSettings] = useState<ReminderSettingListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [previewSetting, setPreviewSetting] = useState<ReminderSettingListItem | null>(null);
  const [practiceName, setPracticeName] = useState<string>('Your Practice');
  const [practicePhone, setPracticePhone] = useState<string>('(555) 123-4567');
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [reminderToDelete, setReminderToDelete] = useState<ReminderSettingListItem | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    loadSettings();
    loadPractice();
  }, []);

  const loadPractice = async () => {
    if (!user?.practice_id) return;
    try {
      const token = (localStorage.getItem('access_token') || localStorage.getItem('token'));
      const response = await fetch(`${API_BASE_URL}/api/v1/practices/${user.practice_id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const practice = await response.json();
        setPracticeName(practice.name || 'Your Practice');
        setPracticePhone(practice.phone || '(555) 123-4567');
      }
    } catch (err) {
      console.error('Failed to fetch practice:', err);
    }
  };

  const loadSettings = async () => {
    if (!user?.practice_id) return;

    setLoading(true);
    try {
      const token = (localStorage.getItem('access_token') || localStorage.getItem('token'));
      const headers = { 'Authorization': `Bearer ${token}` };

      const [appointmentResponse, noVisitResponse] = await Promise.all([
        fetch(API_ENDPOINTS.APPOINTMENT_REMINDER_SETTINGS.LIST(user.practice_id), { headers }),
        fetch(API_ENDPOINTS.NO_VISIT_REMINDER_SETTINGS.LIST(user.practice_id), { headers }),
      ]);

      if (!appointmentResponse.ok) throw new Error('Failed to load appointment settings');
      if (!noVisitResponse.ok) throw new Error('Failed to load Appt Due settings');

      const appointmentData: AppointmentReminderSetting[] = await appointmentResponse.json();
      const noVisitData: NoVisitReminderSetting[] = await noVisitResponse.json();

      const merged: ReminderSettingListItem[] = [
        ...appointmentData.map(setting => ({ ...setting, reminder_type: 'appointment' as const })),
        ...noVisitData.map(setting => ({ ...setting, reminder_type: 'no_visit' as const })),
      ];

      merged.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
      setSettings(merged);
    } catch (err) {
      setError('Failed to load reminder settings');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const openDeleteModal = (setting: ReminderSettingListItem) => {
    setReminderToDelete(setting);
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!user?.practice_id || !reminderToDelete) return;
    
    setIsDeleting(true);
    try {
      const deleteUrl = reminderToDelete.reminder_type === 'appointment'
        ? API_ENDPOINTS.APPOINTMENT_REMINDER_SETTINGS.DELETE(reminderToDelete.id)
        : API_ENDPOINTS.NO_VISIT_REMINDER_SETTINGS.DELETE(reminderToDelete.id);
      const response = await fetch(deleteUrl, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${(localStorage.getItem('access_token') || localStorage.getItem('token'))}`,
        },
      });

      if (!response.ok) throw new Error('Failed to delete');
      await loadSettings();
      setDeleteModalOpen(false);
      setReminderToDelete(null);
    } catch (err) {
      setError('Failed to delete reminder');
      console.error(err);
    } finally {
      setIsDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {error && (
        <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center gap-2 text-red-700 dark:text-red-400">
          <AlertCircle className="w-4 h-4" />
          {error}
        </div>
      )}

      {/* Reminders List */}
      {settings.length === 0 ? (
        <div className="text-center py-12">
          <Bell className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-dark-text-primary mb-2">
            No reminders configured
          </h3>
          <p className="text-gray-500 dark:text-dark-text-muted mb-4">
            Add a reminder to notify pet owners before appointments or when they have not visited recently.
          </p>
        </div>
      ) : (
        <div className="bg-white dark:bg-dark-bg-secondary rounded-lg border border-gray-200 dark:border-dark-border overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-dark-bg-tertiary">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-dark-text-muted uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-dark-text-muted uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-dark-text-muted uppercase tracking-wider">
                  Timing
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-dark-text-muted uppercase tracking-wider">
                  Method
                </th>
                <th className="px-6 py-3 text-center text-xs font-semibold text-gray-500 dark:text-dark-text-muted uppercase tracking-wider">
                  Preview
                </th>
                <th className="px-6 py-3 text-center text-xs font-semibold text-gray-500 dark:text-dark-text-muted uppercase tracking-wider">
                  Delete
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-dark-border">
              {settings.map((setting) => (
                <tr 
                  key={setting.id} 
                  onClick={() => navigate(`/dashboard/set-and-forget/edit/reminder/${setting.id}?type=${setting.reminder_type}`)}
                  className="hover:bg-gray-50 dark:hover:bg-dark-bg-tertiary cursor-pointer"
                >
                  <td className="px-6 py-4">
                    {setting.is_enabled ? (
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                        <Check className="w-3 h-3" />
                        Active
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400">
                        Disabled
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm font-medium text-gray-900 dark:text-dark-text-primary">
                      {setting.reminder_type === 'appointment' ? 'Before appointment' : 'Needs appointment'}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-gray-400" />
                      <span className="text-sm font-medium text-gray-900 dark:text-dark-text-primary">
                        {setting.reminder_type === 'appointment'
                          ? formatMinutes(setting.minutes_before)
                          : `Has not visited in ${formatDuration(setting.inactivity_minutes)} (daily check)`}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-1 text-gray-600 dark:text-dark-text-secondary">
                      {getDeliveryIcon(setting.delivery_method)}
                      <span className="text-sm ml-1">{setting.delivery_method}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <button
                      onClick={(e) => { e.stopPropagation(); setPreviewSetting(setting); }}
                      className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                      title="Preview"
                    >
                      <Eye className="w-4 h-4 inline" />
                    </button>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <button
                      onClick={(e) => { e.stopPropagation(); openDeleteModal(setting); }}
                      className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                    >
                      <Trash2 className="w-4 h-4 inline" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Preview Modal */}
      <PreviewModal
        isOpen={!!previewSetting}
        onClose={() => setPreviewSetting(null)}
        deliveryMethod={normalizeDeliveryMethod(previewSetting?.delivery_method)}
        messageTemplate={previewSetting?.message_template || null}
        templateId={previewSetting?.template_id || null}
        title={previewSetting?.reminder_type === 'no_visit' ? 'Appt Due Reminder Preview' : 'Appointment Reminder Preview'}
        practiceName={practiceName}
        practicePhone={practicePhone}
      />

      {/* Delete Confirmation Modal */}
      {deleteModalOpen && reminderToDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setDeleteModalOpen(false)} />
          <div className="relative bg-white dark:bg-dark-bg-secondary rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-full">
                <Trash2 className="w-5 h-5 text-red-600 dark:text-red-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-dark-text-primary">
                Delete Reminder
              </h3>
            </div>
            <p className="text-gray-600 dark:text-dark-text-secondary mb-6">
              Are you sure you want to delete this reminder? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setDeleteModalOpen(false);
                  setReminderToDelete(null);
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-dark-text-primary bg-white dark:bg-dark-bg-tertiary border border-gray-300 dark:border-dark-border rounded-lg hover:bg-gray-50 dark:hover:bg-dark-bg-hover"
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                disabled={isDeleting}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
              >
                {isDeleting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                    Deleting...
                  </>
                ) : (
                  'Delete'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// Form Triggers Tab (list view only)
// ============================================================================

const FormTriggersTab: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [triggers, setTriggers] = useState<FormTriggerWithTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [previewTrigger, setPreviewTrigger] = useState<FormTriggerWithTemplate | null>(null);
  const [practiceName, setPracticeName] = useState<string>('Your Practice');
  const [practicePhone, setPracticePhone] = useState<string>('(555) 123-4567');
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [triggerToDelete, setTriggerToDelete] = useState<FormTriggerWithTemplate | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    loadData();
    loadPractice();
  }, []);

  const loadPractice = async () => {
    if (!user?.practice_id) return;
    try {
      const token = (localStorage.getItem('access_token') || localStorage.getItem('token'));
      const response = await fetch(`${API_BASE_URL}/api/v1/practices/${user.practice_id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const practice = await response.json();
        setPracticeName(practice.name || 'Your Practice');
        setPracticePhone(practice.phone || '(555) 123-4567');
      }
    } catch (err) {
      console.error('Failed to fetch practice:', err);
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      // Load all form templates
      const templatesData = await getFormTemplates(true);

      // Load triggers for each template
      const allTriggers: FormTriggerWithTemplate[] = [];
      for (const template of templatesData) {
        const templateTriggers = await getFormTriggers(template.id);
        for (const trigger of templateTriggers) {
          allTriggers.push({
            ...trigger,
            form_template_name: template.name,
            form_template_id: template.id,
          });
        }
      }
      setTriggers(allTriggers);
    } catch (err) {
      setError('Failed to load triggers');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const openDeleteModal = (trigger: FormTriggerWithTemplate) => {
    setTriggerToDelete(trigger);
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!triggerToDelete) return;
    
    setIsDeleting(true);
    try {
      await deleteFormTrigger(triggerToDelete.id);
      await loadData();
      setDeleteModalOpen(false);
      setTriggerToDelete(null);
    } catch (err) {
      setError('Failed to delete trigger');
      console.error(err);
    } finally {
      setIsDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {error && (
        <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center gap-2 text-red-700 dark:text-red-400">
          <AlertCircle className="w-4 h-4" />
          {error}
        </div>
      )}

      {/* Triggers List */}
      {triggers.length === 0 ? (
        <div className="text-center py-12">
          <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-dark-text-primary mb-2">
            No triggers configured
          </h3>
          <p className="text-gray-500 dark:text-dark-text-muted mb-4">
            Add a trigger to automatically send forms before appointments.
          </p>
        </div>
      ) : (
        <div className="bg-white dark:bg-dark-bg-secondary rounded-lg border border-gray-200 dark:border-dark-border overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-dark-bg-tertiary">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-dark-text-muted uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-dark-text-muted uppercase tracking-wider">
                  Form Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-dark-text-muted uppercase tracking-wider">
                  Timing
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-dark-text-muted uppercase tracking-wider">
                  Method
                </th>
                <th className="px-6 py-3 text-center text-xs font-semibold text-gray-500 dark:text-dark-text-muted uppercase tracking-wider">
                  Form
                </th>
                <th className="px-6 py-3 text-center text-xs font-semibold text-gray-500 dark:text-dark-text-muted uppercase tracking-wider">
                  Preview
                </th>
                <th className="px-6 py-3 text-center text-xs font-semibold text-gray-500 dark:text-dark-text-muted uppercase tracking-wider">
                  Delete
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-dark-border">
              {triggers.map((trigger) => (
                <tr 
                  key={trigger.id} 
                  onClick={() => navigate(`/dashboard/set-and-forget/edit/trigger/${trigger.id}`)}
                  className="hover:bg-gray-50 dark:hover:bg-dark-bg-tertiary cursor-pointer"
                >
                  <td className="px-6 py-4">
                    {trigger.is_enabled ? (
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                        <Check className="w-3 h-3" />
                        Active
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400">
                        Disabled
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm font-medium text-gray-900 dark:text-dark-text-primary">
                      {trigger.form_template_name}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-gray-400" />
                      <span className="text-sm text-gray-900 dark:text-dark-text-primary">
                        {formatMinutes(trigger.minutes_before)}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-1 text-gray-600 dark:text-dark-text-secondary">
                      {getDeliveryIcon(trigger.delivery_method)}
                      <span className="text-sm ml-1">{trigger.delivery_method}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <button
                      onClick={(e) => { 
                        e.stopPropagation(); 
                        navigate(`/settings/forms?highlight=${trigger.form_template_id}`);
                      }}
                      className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-teal-600 hover:text-teal-800 dark:text-teal-400 dark:hover:text-teal-300 bg-teal-50 hover:bg-teal-100 dark:bg-teal-900/20 dark:hover:bg-teal-900/30 rounded transition-colors"
                      title="View Form"
                    >
                      <ExternalLink className="w-3 h-3" />
                      View
                    </button>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <button
                      onClick={(e) => { e.stopPropagation(); setPreviewTrigger(trigger); }}
                      className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                      title="Preview"
                    >
                      <Eye className="w-4 h-4 inline" />
                    </button>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <button
                      onClick={(e) => { e.stopPropagation(); openDeleteModal(trigger); }}
                      className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                    >
                      <Trash2 className="w-4 h-4 inline" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Preview Modal */}
      <PreviewModal
        isOpen={!!previewTrigger}
        onClose={() => setPreviewTrigger(null)}
        deliveryMethod={normalizeDeliveryMethod(previewTrigger?.delivery_method)}
        messageTemplate={previewTrigger?.message_template || null}
        templateId={previewTrigger?.template_id || null}
        title={`Form Trigger Preview: ${previewTrigger?.form_template_name || ''}`}
        practiceName={practiceName}
        practicePhone={practicePhone}
      />

      {/* Delete Confirmation Modal */}
      {deleteModalOpen && triggerToDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setDeleteModalOpen(false)} />
          <div className="relative bg-white dark:bg-dark-bg-secondary rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-full">
                <Trash2 className="w-5 h-5 text-red-600 dark:text-red-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-dark-text-primary">
                Delete Trigger
              </h3>
            </div>
            <p className="text-gray-600 dark:text-dark-text-secondary mb-6">
              Are you sure you want to delete this form trigger? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setDeleteModalOpen(false);
                  setTriggerToDelete(null);
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-dark-text-primary bg-white dark:bg-dark-bg-tertiary border border-gray-300 dark:border-dark-border rounded-lg hover:bg-gray-50 dark:hover:bg-dark-bg-hover"
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                disabled={isDeleting}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
              >
                {isDeleting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                    Deleting...
                  </>
                ) : (
                  'Delete'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// Overdue Vaccines Tab
// ============================================================================

interface OverdueVaccineTrigger {
  id: string;
  practice_id: string;
  days_overdue: number;
  delivery_method: string;
  is_enabled: boolean;
  is_annual_summary: boolean;
  message_template: string | null;
  template_id: string | null;
  created_at: string;
  updated_at: string;
}

const OverdueVaccinesTab: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [triggers, setTriggers] = useState<OverdueVaccineTrigger[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [triggerToDelete, setTriggerToDelete] = useState<OverdueVaccineTrigger | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [previewTrigger, setPreviewTrigger] = useState<OverdueVaccineTrigger | null>(null);
  const [practiceName, setPracticeName] = useState<string>('Your Practice');
  const [practicePhone, setPracticePhone] = useState<string>('(555) 123-4567');

  // Fetch practice info for preview
  useEffect(() => {
    const fetchPracticeInfo = async () => {
      if (!user?.practice_id) return;
      try {
        const token = (localStorage.getItem('access_token') || localStorage.getItem('token'));
        const response = await fetch(`${API_BASE_URL}/api/v1/practices/${user.practice_id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const practice = await response.json();
          setPracticeName(practice.name || 'Your Practice');
          setPracticePhone(practice.phone || '(555) 123-4567');
        }
      } catch (err) {
        console.error('Failed to fetch practice info:', err);
      }
    };
    fetchPracticeInfo();
  }, [user?.practice_id]);

  const fetchTriggers = async () => {
    const token = (localStorage.getItem('access_token') || localStorage.getItem('token'));
    if (!token) {
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch(`${API_BASE_URL}/api/v1/overdue-vaccine-triggers`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch overdue vaccine triggers');
      }
      
      const data = await response.json();
      setTriggers(data);
    } catch (err) {
      console.error('Error fetching triggers:', err);
      setError(err instanceof Error ? err.message : 'Failed to load triggers');
    } finally {
      setLoading(false);
    }
  };

  // Refetch when navigating back to this tab (location.key changes on navigation)
  useEffect(() => {
    fetchTriggers();
  }, [location.key]);

  const handleDelete = async () => {
    if (!triggerToDelete) return;
    
    const token = (localStorage.getItem('access_token') || localStorage.getItem('token')) || localStorage.getItem('token');
    if (!token) return;
    
    try {
      setDeleting(true);
      
      const response = await fetch(
        `${API_BASE_URL}/api/v1/overdue-vaccine-triggers/${triggerToDelete.id}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );
      
      if (!response.ok) {
        throw new Error('Failed to delete trigger');
      }
      
      setTriggers(triggers.filter(t => t.id !== triggerToDelete.id));
      setDeleteModalOpen(false);
      setTriggerToDelete(null);
    } catch (err) {
      console.error('Error deleting trigger:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete trigger');
    } finally {
      setDeleting(false);
    }
  };

  const formatDaysOverdue = (minutes: number): string => {
    if (minutes === 0) return 'Immediately Overdue';
    // Now stored as minutes, use convertFromMinutes to display
    const { value, unit } = convertFromMinutes(minutes);
    if (unit === 'days') {
      return `${value} day${value !== 1 ? 's' : ''} after overdue`;
    } else if (unit === 'hours') {
      return `${value} hour${value !== 1 ? 's' : ''} after overdue`;
    }
    return `${value} minute${value !== 1 ? 's' : ''} after overdue`;
  };

  const getDeliveryMethodLabel = (method: string): string => {
    switch (method?.toUpperCase()) {
      case 'EMAIL': return 'Email';
      case 'EMAIL_HTML': return 'HTML Email';
      case 'EMAIL_PLAIN': return 'Plain Email';
      case 'SMS': return 'SMS';
      case 'EMAIL_HTML': return 'HTML Email';
      default: return method;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-red-600 dark:text-red-400">{error}</p>
          <button
            onClick={fetchTriggers}
            className="mt-2 text-sm text-red-600 dark:text-red-400 underline"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4">
      {/* Triggers List */}
      {triggers.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 dark:bg-dark-card rounded-lg border border-gray-200 dark:border-dark-border">
          <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-dark-text-primary mb-2">
            No Overdue Reminders Configured
          </h3>
          <p className="text-gray-500 dark:text-dark-text-muted mb-4">
            Set up automated reminders when vaccines become overdue
          </p>
          <button
            onClick={() => navigate('/dashboard/set-and-forget/new/overdue')}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add First Overdue Reminder
          </button>
        </div>
      ) : (
        <div className="bg-white dark:bg-dark-bg-secondary rounded-lg border border-gray-200 dark:border-dark-border overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-dark-bg-tertiary">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-dark-text-muted uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-dark-text-muted uppercase tracking-wider">
                  Timing
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-dark-text-muted uppercase tracking-wider">
                  Method
                </th>
                <th className="px-6 py-3 text-center text-xs font-semibold text-gray-500 dark:text-dark-text-muted uppercase tracking-wider">
                  Preview
                </th>
                <th className="px-6 py-3 text-center text-xs font-semibold text-gray-500 dark:text-dark-text-muted uppercase tracking-wider">
                  Delete
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-dark-border">
              {triggers
                .sort((a, b) => a.days_overdue - b.days_overdue)
                .map((trigger) => (
                  <tr
                    key={trigger.id}
                    onClick={() => navigate(`/dashboard/set-and-forget/edit/overdue/${trigger.id}`)}
                    className="hover:bg-gray-50 dark:hover:bg-dark-bg-tertiary cursor-pointer"
                  >
                    <td className="px-6 py-4">
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                        <Check className="w-3 h-3" />
                        Active
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-gray-400" />
                        <span className="text-sm font-medium text-gray-900 dark:text-dark-text-primary">
                          {formatDaysOverdue(trigger.days_overdue)}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-1 text-gray-600 dark:text-dark-text-secondary">
                        {trigger.delivery_method?.toUpperCase() === 'EMAIL' || trigger.delivery_method?.toUpperCase() === 'EMAIL_HTML' || trigger.delivery_method?.toUpperCase() === 'EMAIL_PLAIN' ? (
                          <Mail className="w-4 h-4" />
                        ) : (
                          <MessageSquare className="w-4 h-4" />
                        )}
                        <span className="text-sm ml-1">{getDeliveryMethodLabel(trigger.delivery_method)}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setPreviewTrigger(trigger);
                        }}
                        className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                        title="Preview"
                      >
                        <Eye className="w-4 h-4 inline" />
                      </button>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setTriggerToDelete(trigger);
                          setDeleteModalOpen(true);
                        }}
                        className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4 inline" />
                      </button>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteModalOpen && triggerToDelete && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-dark-card rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-dark-text-primary mb-2">
              Delete Overdue Reminder?
            </h3>
            <p className="text-gray-500 dark:text-dark-text-muted mb-4">
              This will delete the "{formatDaysOverdue(triggerToDelete.days_overdue)}" 
              reminder and cancel any scheduled notifications. This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setDeleteModalOpen(false);
                  setTriggerToDelete(null);
                }}
                className="px-4 py-2 text-gray-600 dark:text-dark-text-muted hover:text-gray-800 dark:hover:text-dark-text-primary"
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      <PreviewModal
        isOpen={!!previewTrigger}
        onClose={() => setPreviewTrigger(null)}
        deliveryMethod={normalizeDeliveryMethod(previewTrigger?.delivery_method)}
        messageTemplate={previewTrigger?.message_template || null}
        templateId={previewTrigger?.template_id || null}
        title={`Overdue Vaccine Reminder Preview: ${formatDaysOverdue(previewTrigger?.days_overdue || 0)}`}
        practiceName={practiceName}
        practicePhone={practicePhone}
      />
    </div>
  );
};

export default SetAndForgetPage;
